import './Battle.sass';

import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useRoute, useRouter } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';
import { useTimer } from 'react-timer-hook';
import uniqBy from 'lodash/uniqBy';

import { Block, Button, Header, Placeholder, ScrollView, Tabs, TabsItem } from '$uikit';
import { Loader, ShareSheet, UnitName, UnitPhoto } from '$shared/components';
import { useCurrentLang, useCurrentUserInfo, useTranslator } from '$hooks';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import {
  AttachLink,
  AuthPopoverState,
  BattleDonateModel,
  CurrencyEnum,
} from '$store/models';
import { mainActions, mainSelector } from '$store/main';
import { classNames, formatCurrency, statReachGoal } from '$utils';
import { balanceActions } from '$store/balance';
import { PROFILE, SUBSCRIPTION_ADULT } from '$shared/constants/pages';
import { IcLike24, IcShare24 } from '$assets';
import { authSelector } from '$store/auth';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { profileActions } from '$store/profile';
import { goTo, showModal } from '$navigation/helper';
import { battleActions, battleSelector } from '$store/battle';
import { LanguageTags } from '$shared/constants';

export const Battle: FC = () => {
  const { route } = useRoute();
  const battleId = route.params.id;

  const t = useTranslator();
  const dispatch = useDispatch();
  const { users } = useSelector(mainSelector);
  const router = useRouter();
  const { isLogged } = useSelector(authSelector);
  const user = useCurrentUserInfo();
  const currentLang = useCurrentLang();

  const linkTerms = useMemo(() => {
    return currentLang.key === LanguageTags.ru
      ? '/battle_terms_ru.html'
      : '/battle_terms.html';
  }, [currentLang.key]);

  const [loadingState, setLoadingState] = useState('loading');
  const [info, setInfo] = useState<null | {
    description: string;
    donates: BattleDonateModel[];
    oneUserId: number;
    twoUserId: number;
    oneUserPhoto: string;
    twoUserPhoto: string;
    endAt: number;
    oneUserAmount: number;
    twoUserAmount: number;
    levels: string[];
    oneUserFlowBattleId: number;
    twoUserFlowBattleId: number;
  }>();
  const [tab, setTab] = useState('top');
  const { content } = useSelector(battleSelector);

  const timer = useTimer({
    autoStart: false,
    expiryTimestamp: new Date(0),
  });

  const loadPage = useCallback(() => {
    setLoadingState('loading');
    Api.get(`/battle/${battleId}`)
      .then((resp) => {
        const data: any = resp;
        dispatch(mainActions.setUsers(data.users));
        setInfo({
          description: data.description,
          donates: data.donates,
          oneUserId: data.oneUserId,
          twoUserId: data.twoUserId,
          oneUserPhoto: data.oneUserPhoto,
          twoUserPhoto: data.twoUserPhoto,
          endAt: data.endAt,
          oneUserAmount: data.oneUserAmount,
          twoUserAmount: data.twoUserAmount,
          levels: data.levels,
          oneUserFlowBattleId: data.oneUserFlowBattleId,
          twoUserFlowBattleId: data.twoUserFlowBattleId,
        });
        dispatch(
          battleActions.setBattleContent({
            content: data.oneContent.concat(data.twoContent),
          }),
        );
        timer.restart(new Date(data.endAt * 1000));
        setLoadingState('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingState('failed');
      });
  }, [battleId, timer]);

  useEffect(() => {
    statReachGoal('battle_view');

    const interval = setInterval(() => {
      loadPage();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const isEnded = useMemo(() => {
    if (!info) {
      return false;
    }

    return Date.now() / 1000 >= info.endAt;
  }, [info, timer.isRunning]);

  const donateHandle = useCallback(
    (userId: number) => {
      if (isEnded || !info) {
        return;
      }

      if (user.id === info.oneUserId || user.id === info.twoUserId) {
        dispatch(toastActions.setToastFail("You can't vote in your own battle"));
        return;
      }

      statReachGoal('battle_donate_press');

      if (!isLogged) {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      }

      dispatch(
        balanceActions.setBattleDonateData({
          battleId,
          userId,
        }),
      );
    },
    [battleId, isEnded, isLogged, user, info],
  );

  // useEffect(() => {
  //   if (route.params && route.params.show_donate_form > 0 && info) {
  //     donateHandle(+route.params.show_donate_form);
  //   }
  // }, []);

  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
  }, []);

  const goToProfile = useCallback((userId: number) => {
    router.navigate(PROFILE, { id: userId });
  }, []);

  useEffect(() => {
    loadPage();
  }, []);

  const percent = useMemo(() => {
    if (!info) {
      return {
        onePercent: 50,
        twoPercent: 50,
      };
    }

    const total = info.oneUserAmount + info.twoUserAmount;
    if (total <= 0) {
      return {
        onePercent: 50,
        twoPercent: 50,
      };
    }

    const onePercent = Math.round((info.oneUserAmount / total) * 100);
    const twoPercent = Math.round((info.twoUserAmount / total) * 100);

    return {
      onePercent: parseInt(onePercent + '', 10),
      twoPercent: parseInt(twoPercent + '', 10),
    };
  }, [info]);

  const isDeadHeat = useMemo(() => {
    return false;
    //return Math.abs(percent.onePercent - percent.twoPercent) <= 10;
  }, [percent]);

  const history = useMemo(() => {
    if (!info) {
      return [];
    }

    if (tab === 'history') {
      return info.donates;
    }

    let amounts: { [index: number]: number } = {};
    for (let item of info.donates) {
      if (!amounts[item.userId]) {
        amounts[item.userId] = 0;
      }
      amounts[item.userId] += item.amount;
    }

    let result: BattleDonateModel[] = [];
    for (let uid in amounts) {
      result.push({
        id: +uid,
        userId: +uid,
        anonymous: +uid < 0,
        amount: amounts[uid],
        createdAt: 0,
        toUserId: 0,
      });
    }

    result.sort((a, b) => b.amount - a.amount);

    return result;
  }, [tab, info]);

  const handleShare = useCallback(() => {
    const link: AttachLink = {
      type: 'battle',
      object: {
        id: battleId,
        title: 'Battle',
        description: 'Vote for your favorite model',
        imageUrl: 'https://ton.place/assets/logo.png',
      },
    };

    dispatch(
      mainActions.setPopup(
        <ShareSheet title={t('battle_share_title')} link={link} disableInternalShare />,
      ),
    );
  }, [battleId]);

  const handleGifts = useCallback(() => {
    showModal(`photos+battle${battleId}+1`);
  }, []);

  function renderContent() {
    if ((loadingState === 'loading' && !info) || !info) {
      return <Loader isWrapped />;
    }

    if (loadingState === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadPage}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <div className="Battle__cont">
        {isEnded && isDeadHeat && (
          <Block className="BattleInfo">
            <div className="BattleInfo__result__title">{t('battle_dead_heat')}</div>
            <div className="BattleInfo__result">
              <div className="BattleInfo__result__photos">
                <UnitPhoto
                  routeName={null}
                  unit={users[info.oneUserId]}
                  size="large"
                  nftIndicator={false}
                />
                <UnitPhoto
                  routeName={null}
                  unit={users[info.twoUserId]}
                  size="large"
                  nftIndicator={false}
                />
                <div className="BattleInfo__result__like">
                  <IcLike24 />
                </div>
              </div>
              <div className="BattleInfo__result__flapper left" />
              <div className="BattleInfo__result__flapper right" />
            </div>
            {content.length > 0 && (
              <Button style={{ marginTop: 16 }} size="medium" onClick={handleGifts}>
                {t('battle_show_gifts')}
              </Button>
            )}
          </Block>
        )}
        {isEnded && !isDeadHeat && (
          <Block className="BattleInfo">
            <div className="BattleInfo__result__title">{t('battle_winner')}</div>
            <div
              className="BattleInfo__result"
              onClick={() =>
                goToProfile(
                  info.oneUserAmount > info.twoUserAmount
                    ? info.oneUserId
                    : info.twoUserId,
                )
              }
            >
              <UnitPhoto
                routeName={null}
                unit={
                  users[
                    info.oneUserAmount > info.twoUserAmount
                      ? info.oneUserId
                      : info.twoUserId
                  ]
                }
                size="large"
                nftIndicator={false}
              />
              <div className="BattleInfo__result__name">
                <UnitName
                  unit={
                    users[
                      info.oneUserAmount > info.twoUserAmount
                        ? info.oneUserId
                        : info.twoUserId
                    ]
                  }
                />
              </div>
              <div className="BattleInfo__result__flapper left" />
              <div className="BattleInfo__result__flapper right" />
            </div>
            {content.length > 0 && (
              <Button style={{ marginTop: 16 }} size="medium" onClick={handleGifts}>
                {t('battle_show_gifts')}
              </Button>
            )}
          </Block>
        )}
        <Block className="BattlePrize">
          <div className="BattlePrize__title">{t('battle_prize_title')}</div>
          <div className="BattlePrize__amount">
            {formatCurrency(info.oneUserAmount + info.twoUserAmount, CurrencyEnum.EUR)}
          </div>
          <div className="BattlePrize__caption">
            {t('battle_prize_caption')}
            {isEnded && (
              <>
                {' '}
                <a href={linkTerms} target="_blank" rel="noreferrer">
                  {t('battle_donate_terms')}
                </a>
              </>
            )}
          </div>
        </Block>
        <Block className="BattleInfo">
          <div className="BattleInfo__description">{info.description}</div>
          {!isEnded && (
            <div className="BattleInfo__timer">
              <div className="BattleInfo__timer__title">{t('battle_timer_title')}</div>
              <div className="BattleInfo__timer__items">
                <div className="BattleInfo__timer__item">
                  {(timer.days < 10 ? '0' : '') + timer.days}
                  {t('battle_timer_days')}
                </div>
                <div className="BattleInfo__timer__item">
                  {(timer.hours < 10 ? '0' : '') + timer.hours}
                  {t('battle_timer_hours')}
                </div>
                <div className="BattleInfo__timer__item">
                  {(timer.minutes < 10 ? '0' : '') + timer.minutes}
                  {t('battle_timer_minutes')}
                </div>
                <div className="BattleInfo__timer__item">
                  {(timer.seconds < 10 ? '0' : '') + timer.seconds}
                  {t('battle_timer_seconds')}
                </div>
              </div>
            </div>
          )}
        </Block>
        <div className="BattlePhotos">
          <div className="BattlePhotos__icon" />
          <div
            className="BattlePhotos__photo_wrap one"
            onClick={() => donateHandle(info.oneUserId)}
          >
            <div
              className="BattlePhotos__photo"
              style={{
                backgroundImage: `url(${info.oneUserPhoto})`,
              }}
            />
          </div>
          <div
            className="BattlePhotos__photo_wrap two"
            onClick={() => donateHandle(info.twoUserId)}
          >
            <div
              className="BattlePhotos__photo"
              style={{
                backgroundImage: `url(${info.twoUserPhoto})`,
              }}
            />
          </div>
        </div>
        <Block className="BattleDonateForm">
          {!isEnded && isDeadHeat && (
            <div className="BattleDonateForm__deadHeat">
              <div className="BattleDonateForm__deadHeat__title">
                {t('battle_dead_heat_title')}
              </div>
              <div className="BattleDonateForm__deadHeat__caption">
                {t('battle_dead_heat_caption')}
              </div>
            </div>
          )}
          <div className="BattleDonateForm__info">
            <div
              className="BattleDonateForm__info__row"
              onClick={() => goToProfile(info.oneUserId)}
            >
              <div
                className="BattleDonateForm__info__row__photo"
                style={{
                  backgroundImage: `url(${users[info.oneUserId].photo})`,
                }}
              >
                <div
                  className={classNames({
                    BattleDonateForm__info__row__photo__emoji: true,
                    happy: info.oneUserAmount > info.twoUserAmount,
                  })}
                />
              </div>
              <div className="BattleDonateForm__info__row__cont">
                <div className="BattleDonateForm__info__row__name">
                  <UnitName unit={users[info.oneUserId]} namePart="short" />
                </div>
                <div className="BattleDonateForm__info__row__amount">
                  {formatCurrency(info.oneUserAmount, CurrencyEnum.EUR)}
                </div>
              </div>
            </div>
            <div
              className="BattleDonateForm__info__row reverse"
              onClick={() => goToProfile(info.twoUserId)}
            >
              <div className="BattleDonateForm__info__row__cont">
                <div className="BattleDonateForm__info__row__name">
                  <UnitName unit={users[info.twoUserId]} namePart="short" />
                </div>
                <div className="BattleDonateForm__info__row__amount">
                  {formatCurrency(info.twoUserAmount, CurrencyEnum.EUR)}
                </div>
              </div>
              <div
                className="BattleDonateForm__info__row__photo"
                style={{
                  backgroundImage: `url(${users[info.twoUserId].photo})`,
                }}
              >
                <div
                  className={classNames({
                    BattleDonateForm__info__row__photo__emoji: true,
                    happy: info.twoUserAmount > info.oneUserAmount,
                  })}
                />
              </div>
            </div>
          </div>
          <div className="BattleDonateForm__progress">
            <div className="BattleDonateForm__pattern" />
            <div className="BattleDonateForm__progress__items">
              <div
                className="BattleDonateForm__progress__item"
                style={{ flex: percent.onePercent / 100 }}
                onClick={() => donateHandle(info.oneUserId)}
              />
              <div
                className="BattleDonateForm__progress__item"
                style={{ flex: percent.twoPercent / 100 }}
                onClick={() => donateHandle(info.twoUserId)}
              />
            </div>
            <div className="BattleDonateForm__progress__percents">
              <div className="BattleDonateForm__progress__percent">
                {percent.onePercent}%
              </div>
              <div className="BattleDonateForm__progress__percent">
                {percent.twoPercent}%
              </div>
            </div>
          </div>
          {!isEnded && (
            <div className="BattleDonateForm__buttons">
              <Button onClick={() => donateHandle(info.oneUserId)}>
                {t('battle_donate')}
              </Button>
              <Button onClick={() => donateHandle(info.twoUserId)}>
                {t('battle_donate')}
              </Button>
            </div>
          )}
          {!isEnded && (
            <div className="BattleDonateForm__info_caption">
              {t('battle_donate_info')}{' '}
              <a href={linkTerms} target="_blank" rel="noreferrer">
                {t('battle_donate_terms')}
              </a>
            </div>
          )}
        </Block>
        {!isEnded && info.oneUserFlowBattleId > 0 && (
          <Block className="Battle__gifts">
            <div className="Battle__gifts__title">{t('battle_bet_title')}</div>
            <div className="Battle__gifts__caption">{t('battle_bet_caption')}</div>
            <div className="BattleDonateForm__buttons">
              <Button
                href={`https://t.me/FlowOfMarketBot/app?startapp=battle_${info.oneUserFlowBattleId}`}
                target="_blank"
              >
                {users[info.oneUserId].firstName}
              </Button>
              <Button
                href={`https://t.me/FlowOfMarketBot/app?startapp=battle_${info.twoUserFlowBattleId}`}
                target="_blank"
              >
                {users[info.twoUserId].firstName}
              </Button>
            </div>
          </Block>
        )}
        <Block className="Battle__gifts">
          <div className="Battle__gifts__title">{t('battle_gift_title')}</div>
          <div className="Battle__gifts__caption">{t('battle_gift_caption')}</div>
          <div className="Battle__gifts__items">
            <div className="Battle__gifts__item">
              <div className="Battle__gifts__item__amount">
                {formatCurrency(+info.levels[0], CurrencyEnum.EUR)}+
              </div>
              <div className="Battle__gifts__item__caption">
                {t(+battleId === 4 ? 'battle_gift1_title_battle4' : 'battle_gift1_title')}
              </div>
            </div>
            <div className="Battle__gifts__item">
              <div className="Battle__gifts__item__amount">
                {formatCurrency(+info.levels[1], CurrencyEnum.EUR)}+
              </div>
              <div className="Battle__gifts__item__caption">
                {t(+battleId === 4 ? 'battle_gift2_title_battle4' : 'battle_gift2_title')}
              </div>
            </div>
          </div>
          <div className="Battle__gifts__caption">{t('battle_gift_caption2')}</div>
          {battleId > 8 ? (
            <div
              className="Battle__gifts__items"
              onClick={() => goTo(SUBSCRIPTION_ADULT)}
            >
              <div className="Battle__gifts__item">
                <div className="Battle__gifts__item__amount">
                  {formatCurrency(10, CurrencyEnum.EUR)}+
                </div>
                <div className="Battle__gifts__item__caption">
                  {t('battle_gift3_sponsor_membership_title')}
                </div>
              </div>
            </div>
          ) : (
            <div className="Battle__gifts__items">
              <div className="Battle__gifts__item">
                <div className="Battle__gifts__item__amount">
                  {formatCurrency(8, CurrencyEnum.EUR)}+
                </div>
                <div className="Battle__gifts__item__caption">
                  {t('battle_gift3_title')}
                </div>
              </div>
            </div>
          )}
        </Block>
        {history.length > 0 && (
          <div className="BattleDonateHistory__history">
            <Tabs selected={tab} onChange={handleTabChange}>
              <TabsItem value="top">{t('battle_tab_top')}</TabsItem>
              <TabsItem value="history">{t('battle_tab_history')}</TabsItem>
            </Tabs>
            {history.map((item, i) => {
              const anonymousNumber = uniqBy(
                [...info.donates].reverse().filter((d) => d.anonymous),
                (d) => d.userId,
              ).findIndex((d) => d.userId === item.userId);

              let photo = 'https://ton.place/assets/ava_placeholder.png';
              let name = `Anonymous #${anonymousNumber + 1}`;
              let caption: ReactNode = `#${i + 1}`;
              //let onClick: any;
              if (!item.anonymous) {
                photo = users[item.userId].photo;
                name = `${users[item.userId].firstName}`;
                //onClick = () => goToProfile(item.userId);
              }

              if (tab === 'history') {
                caption = t('battle_history_caption', {
                  name: `${users[item.toUserId].firstName} ${
                    users[item.toUserId].lastName
                  }`,
                });
              }

              return (
                <div className="BattleDonateHistory__item" key={item.id}>
                  <div
                    className="BattleDonateHistory__item__photo"
                    style={{ backgroundImage: `url(${photo})` }}
                  />
                  <div className="BattleDonateHistory__item__cont">
                    <div className="BattleDonateHistory__item__name">{name}</div>
                    <div
                      className={classNames('BattleDonateHistory__item__caption', {
                        [item.toUserId === info.oneUserId ? 'one' : 'two']:
                          tab === 'history',
                      })}
                    >
                      {caption}
                    </div>
                  </div>
                  <div className="BattleDonateHistory__item__amount">
                    {formatCurrency(item.amount, CurrencyEnum.EUR)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Header
        showBackButton
        rightIcons={[
          {
            icon: <IcShare24 width={28} height={28} />,
            onClick: handleShare,
          },
        ]}
      >
        {t('battle_title')}
      </Header>
      <ScrollView>{renderContent()}</ScrollView>
      <AuthPopoverSimple />
    </>
  );
};
